import { useEffect, type RefObject } from "react";

// Elements whose script is running.
const running = new WeakSet<Element>();

/**
 * Runs a page's DOM script (the shop's product, bag, checkout and order scripts in site/src/lib,
 * the site UI primitives in site/src/ui) once the page has hydrated, once for the element it
 * renders. The markup stays what React rendered; the script fills it in as on Astro pages, so the
 * element must not re-render with different markup while it runs.
 *
 * When the element leaves the page, the scripts are told to stop with the event they already
 * listen for (`astro:before-swap`): the cart drops its window listeners, the order page its
 * polling, checkout its payment form, and open popups close. React removes the element before it
 * runs this cleanup; in development, StrictMode's rehearsal of the cleanup leaves it in place, so
 * the script keeps running. Use it for a page's own content, which leaves with its page.
 */
export function usePageScript(root: RefObject<HTMLElement | null>, start: () => unknown) {
  useEffect(() => {
    const element = root.current;
    if (!element) return;
    if (!running.has(element)) {
      running.add(element);
      void start();
    }
    return () => {
      if (element.isConnected) return;
      running.delete(element);
      document.dispatchEvent(new Event("astro:before-swap"));
    };
    // The script runs once for each element it was rendered into.
  }, []);
}
